import vue from 'vue'
import axios from '@/services/axios.js'
import { getHeadersWithAccessToken } from '../../functions'

let dt = new Date()
dt.setDate(dt.getDate() - 365)

function calculateAdjustedBudgetForStore (storeID, regressionParameters, prediction, budget) {
    var predictedBudget = 0
    for (let param of regressionParameters) {
        let p = prediction.find(x => x['store_id'] === storeID)
        let b = budget.find(x => x['store_id'] === storeID)
        if (Object.keys(p.dataload).includes(param.var)) {
            predictedBudget += param.factor * Math.pow(p.dataload[param.var], param.transform)
        } else {
            predictedBudget += param.factor * Math.pow(b.dataload_store[param.var], param.transform)
        }
    }
    return Math.round(predictedBudget)
}

function calculateLastLevelBudgetForStore (storeID, regressionParameters, prediction, budget) {
    var predictedBudget = 0
    for (let param of regressionParameters) {
        let p = prediction.find(x => x['store_id'] === storeID)
        let b = budget.find(x => x['store_id'] === storeID)
        if (Object.keys(p.dataload).includes(param.var)) {
            predictedBudget += param.factor * Math.pow(p.dataload_last_level[param.var], param.transform)
        } else {
            predictedBudget += param.factor * Math.pow(b.dataload_store[param.var], param.transform)
        }
    }
    return Math.round(predictedBudget)
}

const state = {
    budgetGraphScores: {},
    budget: [],
    prediction: {},
    predictionSums: [],
    commercialBudgetVariables: [],
    contextStoreBudgets: [],
    manualTotalHourChange: {},
    levels: [],
    stores: [],
    ticketsOpen: undefined,
    savingError: false,
    minimumCorrected: [],
    savedByUser: false,
    initialTotalHourChange: {},
    analyticsTable: [],
    analyticsTotalTable: []
}

const mutations = {
    'SET_LEVELS' (state, payload) {
        vue.set(state, 'levels', payload)
    },
    'SET_BUDGET_GRAPH_SCORE' (state, payload) {
        vue.set(state.budgetGraphScores, payload.title, payload.value)
    },
    'SET_BUDGET' (state, payload) {
        vue.set(state, 'budget', payload)
        // Get the stores for which the budgets are, so that we can use these for plotting the graph in budget summary
        let stores = []
        for (let b of payload) {
            stores.push(b['store_id'])
        }
        vue.set(state, 'stores', stores)
    },
    'SET_PREDICTION' (state, payload) {
        // First we convert all predictions to integers
        for (let p of payload) {
            const keys = Object.keys(p)
            for (let k of keys) {
                if (k.includes('dataload')) {
                    for (let predictionKey in p[k]) {
                        p[k][predictionKey] = Math.round(p[k][predictionKey])
                    }
                }
            }
        }
        vue.set(state, 'prediction', payload)
    },
    'SET_INITIAL_HOUR_CHANGE' (state) {
        let initialTotalHourChange = {}
        for (let b of state.budget) {
            let calculatedBudget = calculateLastLevelBudgetForStore(b.store_id, b['regression_settings'], state.prediction, state.budget)
            initialTotalHourChange[b.store_id] = b.hours_last_level - calculatedBudget
        }
        vue.set(state, 'initialTotalHourChange', initialTotalHourChange)
    },
    'SET_PREDICTION_SUMS' (state) {
        // First get the commercial variables form the dataload
        let commercialVars = ['sales_revenue'] // /Fix this
        let regressionParams = []
        for (let b of state.budget) {
            // Get budget given comm params and model and calculate if there have been any changes in the last level
            for (let item of b.regression_settings) {
                if (regressionParams.indexOf(item.var) === -1) {
                    regressionParams.push(item.var)
                }
            }
        }

        for (let item of regressionParams) {
            if (Object.keys(state.prediction[0].dataload_last_level).includes(item)) {
                if (commercialVars.indexOf(item) === -1) {
                    commercialVars.push(item)
                }
            }
        }

        // For all commercial variables get the sums
        let data = []
        for (let item of commercialVars) {
            let i = commercialVars.indexOf(item)
            data[i] = {}
            data[i]['Variable'] = item
            data[i]['id'] = i
            data[i]['child'] = false
            data[i]['Last year'] = 0
            if (Object.keys(state.prediction[0].dataload).includes(item)) {
                data[i]['Predicted'] = 0
                data[i]['Adjusted'] = 0
            }
            for (let j = 0; j < state.prediction.length; j++) {
                data[i]['Last year'] += Math.round(state.prediction[j].dataload_ly[item])
                if (Object.keys(state.prediction[j].dataload).includes(item)) {
                    data[i]['Predicted'] += Math.round(state.prediction[j].dataload_last_level[item])
                    data[i]['Adjusted'] += Math.round(state.prediction[j].dataload[item])
                }
            }
            if (Object.keys(state.prediction[0].dataload).includes(item)) {
                data[i]['Adjusted'] = Math.round(data[i]['Adjusted'])
            }
        }

        data.push({ 'Variable': 'Total hours', 'child': false, 'id': commercialVars.length, 'children': [], 'Last year': state.budget.reduce(function (prev, cur) { return Math.round(prev) + Math.round(cur.hours_ly) }, 0), 'Predicted': state.budget.reduce(function (prev, cur) { return Math.round(prev) + Math.round(cur.hours_last_level) }, 0), 'Adjusted': state.budget.reduce(function (prev, cur) { return Math.round(prev) + Math.round(cur.hours) }, 0) })

        let totalHoursChilds = []
        totalHoursChilds.push({ 'Variable': 'KPU', 'child': true, 'id': commercialVars.length + 1, 'Last year': state.budget.reduce(function (prev, cur) { return parseFloat(prev) + parseFloat(cur.kpuly * (cur.hours_ly / (data.find(x => x.Variable === 'Total hours')['Last year']))) }, 0), 'Predicted': state.budget.reduce(function (prev, cur) { return parseFloat(prev) + parseFloat(cur.kpu * (cur.hours_last_level / (data.find(x => x.Variable === 'Total hours')['Predicted']))) }, 0), 'Adjusted': state.budget.reduce(function (prev, cur) { return parseFloat(prev) + parseFloat(cur.kpu * (cur.hours / (data.find(x => x.Variable === 'Total hours')['Adjusted']))) }, 0) })
        totalHoursChilds.push({ 'Variable': 'CTR', 'child': true, 'id': commercialVars.length + 2, 'Last year': state.budget.reduce(function (prev, cur) { return parseFloat(prev) + 100 * parseFloat((cur.kpuly * cur.hours_ly)) }, 0), 'Predicted': state.budget.reduce(function (prev, cur) { return parseFloat(prev) + 100 * parseFloat((cur.kpu * cur.hours_last_level)) }, 0), 'Adjusted': state.budget.reduce(function (prev, cur) { return parseFloat(prev) + 100 * parseFloat((cur.kpu * cur.hours)) }, 0) })

        // Divide the CTR by the sum of sales revenue
        let ctr = totalHoursChilds.find(x => x.Variable === 'CTR')
        let ctrIdx = totalHoursChilds.indexOf(ctr)
        totalHoursChilds[ctrIdx]['Last year'] = totalHoursChilds[ctrIdx]['Last year'] / data.find(x => x.Variable === 'sales_revenue')['Last year']
        totalHoursChilds[ctrIdx]['Predicted'] = totalHoursChilds[ctrIdx]['Predicted'] / data.find(x => x.Variable === 'sales_revenue')['Predicted']
        totalHoursChilds[ctrIdx]['Adjusted'] = totalHoursChilds[ctrIdx]['Adjusted'] / data.find(x => x.Variable === 'sales_revenue')['Adjusted']

        // Set childs of Total hours
        let tot = data.find(x => x.Variable === 'Total hours')
        let totIdx = data.indexOf(tot)
        data[totIdx]['children'] = totalHoursChilds

        if (regressionParams.indexOf('sales_revenue') === -1) { // sales revenue is not used in model but was only needed to calculate CTR etc.
            let idx = commercialVars.indexOf('sales_revenue')
            commercialVars.splice(idx, 1)
            let x = data.find(x => x.Variable === 'sales_revenue')
            idx = data.indexOf(x)
            data.splice(idx, 1)
        }

        vue.set(state, 'commercialBudgetVariables', commercialVars)
        vue.set(state, 'predictionSums', data)
    },
    'UPDATE_PREDICTIONS_FACTOR' (state, payload) {
        // Given a variable name and factor, update the underlying prediction
        if (state.commercialBudgetVariables.includes(payload.variable)) {
            for (let i = 0; i < state.prediction.length; i++) {
                let value = state.prediction[i].dataload_last_level[payload['variable']]
                let newValue = value * payload['factor']
                vue.set(state.prediction[i].dataload, payload['variable'], newValue)
            }
        }
        // Get the new hours for each store
        for (let i = 0; i < state.budget.length; i++) {
            // Here we do not need to add manualTotalHourChange since this is taken into consideration
            vue.set(state.budget[i], 'hours', calculateAdjustedBudgetForStore(state.budget[i].store_id, state.budget[i]['regression_settings'], state.prediction, state.budget) + state.initialTotalHourChange[state.budget[i]['store_id']])
        }
    },
    'SET_CONTEXT_STORE_BUDGETS' (state, payload) {
        vue.set(state, 'contextStoreBudgets', payload)
    },

    'REVERT_BUDGET_CHANGES' (state, variable) {
        // This mutation is called when someone presses the revert button, this sets commercial back to predicted and total hours to current regression budget
        if (variable === 'Total hours') {
            for (let item in state.manualTotalHourChange) {
                vue.set(state.manualTotalHourChange, item, 0)
            }
        // Set the dataload value back to the value of last_level if button pressed on commercial variable
        } else {
            for (let i in state.prediction) {
                vue.set(state.prediction[i]['dataload'], variable, JSON.parse(JSON.stringify(state.prediction[i]['dataload_last_level'][variable])))
            }
        }
        // Now calculate the new budgets
        for (let i in state.budget) {
            vue.set(state.budget[i], 'hours', calculateAdjustedBudgetForStore(state.budget[i]['store_id'], state.budget[i]['regression_settings'], state.prediction, state.budget) + state.manualTotalHourChange[state.budget[i]['store_id']] + state.initialTotalHourChange[state.budget[i]['store_id']])
        }
    },
    'SET_MANUAL_HOUR_CHANGES' (state, payload) {
        // This function is called when someone changes hours manually it possibly checks for minimum, then sets the budget and updates the manual changes object
        if (Object.keys(payload).includes('store_id')) {
            let b = state.budget.indexOf(state.budget.find(x => x.store_id === payload.store_id))
            let regressionBudget = calculateAdjustedBudgetForStore(state.budget[b]['store_id'], state.budget[b]['regression_settings'], state.prediction, state.budget)
            let newBudget = Math.round((payload.factor * state.budget[b]['hours_last_level']))
            if (payload.initialLoad === true) { // On initial load we need to incorporate already made changes in the budget
                newBudget = Math.round(state.budget[b]['hours'])
            }
            if (payload.updateHours === true) {
                vue.set(state.budget[b], 'hours', newBudget)
            }
            vue.set(state.manualTotalHourChange, state.budget[b]['store_id'], (newBudget - regressionBudget - state.initialTotalHourChange[state.budget[b]['store_id']]))
        } else {
            for (let b in state.budget) {
                let regressionBudget = calculateAdjustedBudgetForStore(state.budget[b]['store_id'], state.budget[b]['regression_settings'], state.prediction, state.budget)
                let newBudget = Math.round((payload.factor * state.budget[b]['hours_last_level']))
                if (payload.initialLoad === true) { // On initial load we need to incorporate already made changes in the budget
                    newBudget = Math.round(state.budget[b]['hours'])
                }
                if (payload.updateHours === true) {
                    vue.set(state.budget[b], 'hours', newBudget)
                }
                vue.set(state.manualTotalHourChange, state.budget[b]['store_id'], (newBudget - regressionBudget - state.initialTotalHourChange[state.budget[b]['store_id']]))
            }
        }
    },
    'CORRECT_MINIMUM' (state) {
        // Used for initial correct after closing budget budget modal and going to store budgets
        for (let b in state.budget) {
            if (state.budget[b].hours < state.budget[b].min_hours) {
                vue.set(state.manualTotalHourChange, state.budget[b]['store_id'], state.budget[b].min_hours - state.budget[b].hours)
                vue.set(state.budget[b], 'hours', state.budget[b]['min_hours'])
                state.minimumCorrected.push(state.budget[b]['store_id'])
            }
        }
    },

    'UPDATE_STORE_HOURS' (state, payload) {
        // This mutation is used when someone presses + or - on the individual store budgets (using an action)
        let newHours = Math.round(JSON.parse(JSON.stringify(state.budget[payload.idx].hours * payload.factor)))
        let delta = Math.round(newHours) - state.budget[payload.idx].hours
        let manual = state.manualTotalHourChange[state.budget[payload.idx].store_id] + Math.round(delta)
        vue.set(state.budget[payload.idx], 'hours', newHours)
        vue.set(state.manualTotalHourChange, state.budget[payload.idx].store_id, manual)
    },
    'SET_SAVED_BY_USER' (state, payload) {
        vue.set(state, 'savedByUser', payload)
    },
    'SET_ANALYTICS_TABLE' (state, payload) {
        if (payload.total === true) {
            vue.set(state, 'analyticsTotalTable', payload.data)
        } else {
            vue.set(state, 'analyticsTable', payload.data)
        }
    }
}

const actions = {
    correctMinimum: ({ commit }) => {
        return commit('CORRECT_MINIMUM')
    },
    getLevels: ({ commit, rootState }) => {
        return axios.get(((rootState.general.devMode === true) ? process.env.VUE_APP_BUDGET_API_BASE_URL_DEV : process.env.VUE_APP_BUDGET_API_BASE_URL) + '/level_names?', { headers: getHeadersWithAccessToken() })
            .then(response => {
                commit('SET_LEVELS', response.data.data)
            })
            .catch(error => console.log(error))
    },
    getBudget: ({ commit, rootState }, payload) => {
        return axios.get(((rootState.general.devMode === true) ? process.env.VUE_APP_BUDGET_API_BASE_URL_DEV : process.env.VUE_APP_BUDGET_API_BASE_URL) + '/budgets?' + payload.route, { headers: getHeadersWithAccessToken() })
            .then(response => {
                commit('SET_BUDGET', response.data.data)
                commit('SET_SAVED_BY_USER', response.data.saved_by_user)
            })
            .catch(error => console.log(error))
    },
    saveBudget: ({ commit, rootState }, payload) => {
        return axios.post(((rootState.general.devMode === true) ? process.env.VUE_APP_BUDGET_API_BASE_URL_DEV : process.env.VUE_APP_BUDGET_API_BASE_URL) + '/budgets?' + payload.route, state.budget, { headers: getHeadersWithAccessToken() })
            .then(response => {
                commit('SET_SAVED_BY_USER', true)
            })
            .catch(error => {
                state.savingError = true
                // eslint-disable-next-line
                setTimeout(() => state.savingError = false, 5000)
                console.log(error)
            })
    },
    savePrediction: ({ commit, rootState }, payload) => {
        return axios.post(((rootState.general.devMode === true) ? process.env.VUE_APP_PREDICTION_API_BASE_URL_DEV : process.env.VUE_APP_PREDICTION_API_BASE_URL) + '/predictions?' + payload.route, state.prediction, { headers: getHeadersWithAccessToken() })
            .then(response => {
            //
            })
            .catch(error => {
                state.savingError = true
                // eslint-disable-next-line
                setTimeout(() => state.savingError = false, 5000)
                console.log(error)
            })
    },
    getPrediction: ({ commit, rootState }, payload) => {
        return axios.get(((rootState.general.devMode === true) ? process.env.VUE_APP_PREDICTION_API_BASE_URL_DEV : process.env.VUE_APP_PREDICTION_API_BASE_URL) + '/predictions?context=true&' + payload.route, { headers: getHeadersWithAccessToken() })
            .then(response => {
                commit('SET_PREDICTION', response.data.data)
            })
            .catch(error => console.log(error))
    },
    getContextStoreBudget: ({ commit, rootState }, payload) => {
        return axios.post(((rootState.general.devMode === true) ? process.env.VUE_APP_DATA_API_BASE_URL_DEV : process.env.VUE_APP_DATA_API_BASE_URL) + '/at/budget/budget/store_context', { 'store_id': payload, 'format': 'aod' }, { headers: getHeadersWithAccessToken() })
            .then(response => {
                commit('SET_CONTEXT_STORE_BUDGETS', response.data.data)
            })
            .catch(error => console.log(error))
    },
    updatePredictionsFactor: ({ commit }, payload) => {
        // If in the main budget component a commercial variable is changed, we update the variable using the factor and set the new sums
        return new Promise((resolve, reject) => {
            if (payload) {
                commit('UPDATE_PREDICTIONS_FACTOR', payload)
                commit('SET_PREDICTION_SUMS', payload)
                resolve()
            } else {
                reject(Error('Something went wrong updating the predictions'))
            }
        })
    },
    revertBudgetChanges: ({ commit, rootState }, payload) => {
        return new Promise((resolve, reject) => {
            if (payload) {
                commit('REVERT_BUDGET_CHANGES', payload)
                commit('SET_PREDICTION_SUMS', payload)
                resolve()
            } else {
                reject(Error('Something went wrong reverting the changes'))
            }
        })
    },
    updateStoreHours: ({ commit, rootState }, payload) => {
        commit('UPDATE_STORE_HOURS', payload)
    }
}

const getters = {
    getLevels: state => { return JSON.parse(JSON.stringify(state.levels)) },
    getStoreIds: state => { return JSON.parse(JSON.stringify(state.stores)) },
    getYear: state => {
        if (state.budget.length > 0) {
            return state.budget[0].year
        }
    },
    getWeek: state => {
        if (state.budget.length > 0) {
            return state.budget[0].week
        }
    }
}

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
}
